'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const revenueProjections = [
{ year: 'Y1', revenue: 3.2, ebitda: -0.8, label: 'Ramp-up', color: '#16A34A' },
{ year: 'Y2', revenue: 8.5, ebitda: 1.8, label: 'EBITDA+', color: '#16A34A' },
{ year: 'Y3', revenue: 13.2, ebitda: 4.1, label: 'Scale', color: '#22C55E' },
{ year: 'Y4', revenue: 15.8, ebitda: 5.2, label: 'Maturity', color: '#FACC15' }];


const teamMembers = [
{
  name: 'Daniel Ochieng',
  title: 'Chief Executive Officer',
  background: 'Aviation-level operational discipline across multi-sector infrastructure projects. 15 years executing complex supply chain systems in East Africa.',
  initials: 'DO',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1373c1c56-1763291648235.png",
  alt: 'Daniel Ochieng, CEO of Batis Poultry, professional headshot in business attire'
},
{
  name: 'Amara Wanjiku',
  title: 'Chief Financial Officer',
  background: 'Proven financial optimization across agricultural and manufacturing sectors. Led $40M+ capital structuring across three portfolio companies.',
  initials: 'AW',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_17c736752-1763300698503.png",
  alt: 'Amara Wanjiku, CFO of Batis Poultry, professional headshot'
},
{
  name: 'Samuel Kipchoge',
  title: 'Head of Production',
  background: '40+ years executing large-scale poultry operations. Managed facilities producing 20M+ eggs annually across Southern and East Africa.',
  initials: 'SK',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png",
  alt: 'Samuel Kipchoge, Head of Production at Batis Poultry, professional headshot'
}];


const maxRevenue = Math.max(...revenueProjections?.map((p) => p?.revenue));

export default function FinancialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="financials"
      ref={sectionRef}
      className="py-32 lg:py-40 bg-off-white"
      aria-labelledby="financials-heading">
      
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Label */}
        <div className="scroll-reveal mb-6">
          <span className="section-label">04 / Financial Outlook</span>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          <div className="scroll-reveal">
            <h2 id="financials-heading" className="font-display text-[clamp(2.5rem,5.5vw,4.5rem)] leading-[0.9] tracking-[-0.04em] text-green-dark">
              EBITDA positive<br />
              <span className="text-green-primary">from Year 2.</span><br />
              30%+ margins.
            </h2>
          </div>
          <div className="scroll-reveal flex items-end" style={{ transitionDelay: '100ms' }}>
            <p className="text-lg text-black/70 leading-relaxed">
              A high-margin, multi-revenue agricultural production asset with rapid profitability inflection. Revenue scales to $15M+ by Year 3 through disciplined production ramp and operational efficiency.
            </p>
          </div>
        </div>

        {/* Revenue chart + key metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-20">
          {/* Bar chart */}
          <div className="scroll-reveal lg:col-span-7">
            <div
              className="rounded-2xl p-8"
              style={{ background: 'linear-gradient(135deg, #0A1F0F, #0F2D18)' }}>
              
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="section-label mb-1">Revenue Projection</p>
                  <p className="text-white/70 text-sm">USD millions · 4-year outlook</p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-green-primary inline-block" />
                    <span className="text-white/70">Revenue</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-yellow-accent inline-block" />
                    <span className="text-white/70">EBITDA</span>
                  </div>
                </div>
              </div>

              {/* Bars */}
              <div className="flex items-end gap-4 h-48">
                {revenueProjections?.map((proj, idx) =>
                <div key={proj?.year} className="flex-1 flex flex-col items-center gap-2">
                    {/* EBITDA bar */}
                    <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                      <div className="flex flex-col items-center gap-1 w-full" style={{ height: '100%', justifyContent: 'flex-end' }}>
                        {/* Revenue bar */}
                        <div
                        className="w-full rounded-t-lg progress-bar"
                        style={{
                          height: `${proj?.revenue / maxRevenue * 100}%`,
                          background: idx === 3 ? 'linear-gradient(180deg, #FACC15, #EAB308)' : 'linear-gradient(180deg, #22C55E, #16A34A)',
                          animationDelay: `${idx * 0.15}s`,
                          minHeight: '8px'
                        }} />
                      
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="font-display text-lg text-white tracking-tight">${proj?.revenue}M</p>
                      <p className="text-xs text-white/30 font-mono-custom">{proj?.year}</p>
                      <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block"
                      style={{
                        background: proj?.ebitda > 0 ? 'rgba(22,163,74,0.15)' : 'rgba(255,255,255,0.05)',
                        color: proj?.ebitda > 0 ? '#22C55E' : '#6B7280'
                      }}>
                      
                        {proj?.ebitda > 0 ? `+$${proj?.ebitda}M` : `$(${Math.abs(proj?.ebitda)}M)`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Key financial metrics */}
          <div className="scroll-reveal lg:col-span-5 flex flex-col gap-4" style={{ transitionDelay: '150ms' }}>
            {[
            { label: 'Year 3 Revenue Target', value: '$15M+', note: 'Conservative projection', highlight: true },
            { label: 'EBITDA Margin (Mature)', value: '30%+', note: 'Industry-leading efficiency', highlight: false },
            { label: 'EBITDA Positive', value: 'Year 2', note: 'Rapid profitability inflection', highlight: false },
            { label: 'Revenue Streams', value: '3 Active', note: 'Eggs · Meat · Manure', highlight: false }]?.
            map((metric) =>
            <div
              key={metric?.label}
              className={`rounded-xl p-5 flex items-center justify-between ${metric?.highlight ? 'glass-green' : 'glass-green'}`}>
              
                <div>
                  <p className={`text-xs font-mono-custom uppercase tracking-widest mb-1 ${metric?.highlight ? 'text-yellow-900' : 'text-black/70'}`}>
                    {metric?.label}
                  </p>
                  <p className={`text-xs ${metric?.highlight ? 'text-yellow-900' : 'text-green-primary/50'}`}>{metric?.note}</p>
                </div>
                <div className={`font-display text-2xl tracking-tight ${metric?.highlight ? 'text-yellow-accent' : 'text-green-dark'}`}>
                  {metric?.value}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="divider-green mb-20" />

        {/* Team section */}
        {/* <div className="scroll-reveal mb-10" style={{ transitionDelay: '200ms' }}>
          <span className="section-label">The Team</span>
          <h3 className="font-display text-[clamp(2rem,4vw,3.5rem)] leading-[0.9] tracking-[-0.04em] text-green-dark mt-4">
            Execution operators,<br />
            <span className="text-green-primary">not theoretical founders.</span>
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 stagger-children">
          {teamMembers?.map((member, idx) =>
          <div
            key={member?.name}
            className="scroll-reveal rounded-2xl overflow-hidden hover-lift"
            style={{ transitionDelay: `${idx * 120}ms`, background: '#F0F4F0' }}>
            
              <div className="img-overlay h-56">
                <AppImage
                src={member?.img}
                alt={member?.alt}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 33vw" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-green-dark/80 via-transparent to-transparent z-10" />
              </div>

              <div className="p-6">
                <h4 className="font-display text-xl text-green-dark tracking-tight">{member?.name}</h4>
                <p className="text-green-primary text-sm font-semibold mt-0.5 mb-4">{member?.title}</p>
                <p className="text-sm text-black/70 leading-relaxed">{member?.background}</p>
              </div>
            </div>
          )}
        </div> */}

        {/* Partner credibility bar */}
        <div className="scroll-reveal mt-12 glass-green rounded-2xl p-7" style={{ transitionDelay: '400ms' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
            <div>
              <div className="font-display text-3xl text-green-primary tracking-tight">40+</div>
              <p className="text-sm text-black/70 mt-1">Years partner experience in large-scale poultry operations</p>
            </div>
            <div className="md:border-x border-green-primary/10 md:px-6">
              <div className="font-display text-3xl text-green-primary tracking-tight">EU-grade</div>
              <p className="text-sm text-black/70 mt-1">European-grade equipment standards across all production systems</p>
            </div>
            <div>
              <div className="font-display text-3xl text-green-primary tracking-tight">Global</div>
              <p className="text-sm text-black/70 mt-1">Established supply chain partnerships with international suppliers</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}